import Image from "@/components/ui/image";
import useUserStore, { useIndexStore } from "@/state/user/useUserStore";
import { m, AnimatePresence } from "motion/react";
import React, { useState } from "react";
import { ScrollView } from "react95";
import styled from "styled-components";
import { ChevronDown, ChevronUp } from "lucide-react";

export const StyledMotionDiv = styled(m.div)`
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  box-sizing: border-box;

  @media (min-width: 1280px) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
`;

// Styled components for the folder structure
const FolderHeader = styled.div`
  padding: 12px;
  background-color: #45aa76;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0px;
  width: 100%;
  border-radius: 2px;
  font-size: 20px;
  box-shadow: 1px 1px 0px 0px #000000;
  border: 3px solid #000000;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #5bbc8a;
  }
`;

const FolderContent = styled(m.div)<{ isOpen: boolean }>`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px;
  padding: 24px;
  overflow: hidden;

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const ArrowIcon = styled(m.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
`;

// Add a container for all folders
const FoldersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;

const ProjectComponent = () => {
  // State for tracking which folders are open
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({
    Henlo: false,
    THJ: false,
    Mibera: false,
    Plug: false,
    Baddies: false,
    Bullas: false,
  });

  const [setAssets, textFields, uploadAssets, selectedAssets] = useUserStore(
    (state) => [
      state.setAssets,
      state.textFields,
      state.uploadAssets,
      state.assets,
    ]
  );

  const [lastAssetIndex, setLastAssetIndex] = useIndexStore((state) => [
    state.lastAssetIndex,
    state.setLastAssetIndex,
  ]);

  const allFieldsLength =
    textFields.length + selectedAssets.length + uploadAssets.length;

  const setAssetsHandler = (img: string) => {
    const id = crypto.randomUUID();
    setAssets((currentAssets) => [
      ...currentAssets,
      {
        id: id,
        img: img,
        flipHorizontal: false,
        flipVertical: false,
        zIndex: allFieldsLength + 1,
        index: lastAssetIndex + 1,
      },
    ]);
    setLastAssetIndex(lastAssetIndex + 1);
  };

  const BaddiesAssets = Array.from(
    { length: 19 },
    (_, i) => `/Mememaker/baddies/baddies${i + 1}.png`
  );

  const BullasAssets = Array.from(
    { length: 37 },
    (_, i) => `/Mememaker/bullas/bullas${i + 1}.png`
  );

  const PlugAssets = Array.from(
    { length: 24 },
    (_, i) => `/Mememaker/plug/assets/plug${i + 1}.png`
  ).filter((_, index) => index !== 22); // Remove the 23rd item (index 22)

  const HenloAssets = [
    // Original henlo assets
    ...Array.from(
      { length: 80 },
      (_, i) => `/Mememaker/henlo2/asset/asset${i + 1}.png`
    ),
    // Banner assets
    ...Array.from(
      { length: 43 },
      (_, i) => `/Mememaker/banners/asset/asset${i + 1}.png`
    ),
  ];

  const MiberaAssets = Array.from(
    { length: 65 },
    (_, i) => `/Mememaker/milady/asset${i + 1}.${i < 16 ? "PNG" : "png"}`
  );

  const toggleFolder = (folderName: string) => {
    setOpenFolders((prev) => ({
      ...prev,
      [folderName]: !prev[folderName],
    }));
  };

  // Define folder structure
  const folders = [
    {
      name: "Henlo",
      assets: HenloAssets,
      logo: "/Mememaker/henlo2/asset/asset9.png",
    },
    {
      name: "Mibera",
      assets: MiberaAssets,
      logo: "/Mememaker/logos/green_logo.png",
    },
    {
      name: "Plug",
      assets: PlugAssets,
      logo: "/Mememaker/plug/assets/plug12.png",
    },
    {
      name: "Bullas",
      assets: BullasAssets,
      logo: "/Mememaker/logos/red_logo.png",
    },
    {
      name: "Baddies",
      assets: BaddiesAssets,
      logo: "/Mememaker/logos/purple_logo.png",
    },
  ];

  return (
    <ScrollView
      style={{
        height: `calc(100% - 68px)`,
        margin: "0",
        padding: "0",
      }}
    >
      <StyledMotionDiv>
        <FoldersContainer>
          {folders.map((folder) => (
            <div key={folder.name}>
              <FolderHeader onClick={() => toggleFolder(folder.name)}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      position: "relative",
                    }}
                  >
                    <Image
                      src={folder.logo}
                      alt=""
                      fill
                      style={{
                        objectFit: "contain",
                      }}
                    />
                  </div>
                  <span>{folder.name}</span>
                </div>
                <ArrowIcon
                  animate={{ rotate: openFolders[folder.name] ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <ChevronDown size={24} />
                </ArrowIcon>
              </FolderHeader>
              <AnimatePresence>
                {openFolders[folder.name] && (
                  <FolderContent
                    isOpen={openFolders[folder.name]}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                      height: "auto",
                      opacity: 1,
                      transition: {
                        height: { duration: 0.3 },
                        opacity: { duration: 0.3 },
                      },
                    }}
                    exit={{
                      height: 0,
                      opacity: 0,
                      transition: {
                        height: { duration: 0.3 },
                        opacity: { duration: 0.2 },
                      },
                    }}
                  >
                    {folder.assets.map((asset: string, index: number) => (
                      <m.div
                        key={index}
                        // initial={{ scale: 0.8, opacity: 0 }}
                        // animate={{ scale: 1, opacity: 1 }}
                        // transition={{ duration: 0.2, delay: index * 0.03 }}
                        style={{
                          width: "100%",
                          aspectRatio: "1/1",
                          position: "relative",
                          cursor: "pointer",
                        }}
                        onClick={() => setAssetsHandler(asset)}
                      >
                        <Image
                          draggable={false}
                          src={asset}
                          style={{
                            objectFit: "contain",
                          }}
                          alt=""
                          fill
                        />
                      </m.div>
                    ))}
                  </FolderContent>
                )}
              </AnimatePresence>
            </div>
          ))}
        </FoldersContainer>
      </StyledMotionDiv>
    </ScrollView>
  );
};

export default ProjectComponent;
